/**
 * TodayTile — «I dag» på Hjem (#154, PLAN.md steg 4a).
 *
 * To fliser side om side, som skissen:
 *   - datopanelet (mint → salvie → strå, `PANEL_SURFACE`): ukedag, dagens
 *     dato og antall hendelser i dag. Hele panelet lenker til /kalender.
 *   - et glasskort med dagens hendelser, maks fire (TODAY_EVENTS_MAX) og
 *     «+N til» som også lenker til /kalender.
 *
 * Data og sortering er de samme som EventsTile på kjøkkenskjermen
 * (`useTodayEvents`, `sortTodayEvents`); EventsTile selv restyles ikke her,
 * kjøkkenet får sitt utseende i steg 10.
 *
 * Skissen kaller listen «Kommer», men den viser alle dagens hendelser, også de
 * som er over. Derfor står den eksisterende etiketten «På kalenderen».
 *
 * Panelet er ikke en DatePanel: det er en lenke med stort datotall, mens
 * DatePanel er en seksjon med sidetittel (h1). De deler flaten.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { GLASS_SURFACE } from '../../../components/primitives/Card';
import { PANEL_SURFACE } from '../../../components/primitives/DatePanel';
import {
  EventColorStripe,
  resolveStripeSlot,
} from '../../../components/primitives/EventColorStripe';
import {
  capitalizeFirst,
  formatEventTimeRange,
  formatMonthShort,
  formatWeekday,
} from '../../../i18n/formatters';
import { useTodayEvents } from '../../today/useTodayEvents';
import { TODAY_EVENTS_MAX, sortTodayEvents } from './EventsTile';

/** `today` kommer fra OverviewRoute (`useToday`), som også gir datoen i toppen. */
export function TodayTile({ today }: { today: Date }): ReactNode {
  const { t } = useTranslation();
  const { data: events = [] } = useTodayEvents();

  const sorted = sortTodayEvents(events, (e) => e.starts_at);
  const visible = sorted.slice(0, TODAY_EVENTS_MAX);
  const overflow = sorted.length - visible.length;
  const countLabel =
    sorted.length === 0 ? t('home.today.none') : t('home.today.events', { count: sorted.length });

  return (
    <div className="flex gap-3">
      <Link
        to="/kalender"
        data-testid="home-today-panel"
        aria-label={t('home.today.aria', { events: countLabel })}
        className={`${PANEL_SURFACE} flex min-h-38 w-35 shrink-0 flex-col justify-between rounded-lg p-4`}
      >
        <span className="text-control font-bold">{capitalizeFirst(formatWeekday(today))}</span>
        <span className="text-5xl leading-none font-extrabold tracking-tighter tnum">
          {today.getDate()}
          <span className="text-heading-s font-bold tracking-normal"> {formatMonthShort(today)}</span>
        </span>
        <span className="text-control font-semibold">{countLabel}</span>
      </Link>

      <section
        aria-labelledby="home-events-title"
        data-testid="home-events"
        className={`${GLASS_SURFACE} flex min-h-38 min-w-0 flex-1 flex-col gap-2.5 rounded-lg px-4 py-3.5 shadow-md`}
      >
        <h2
          id="home-events-title"
          className="m-0 text-caption font-bold tracking-wider text-ink-2 uppercase"
        >
          {t('dashboard.events.title')}
        </h2>
        {sorted.length === 0 ? (
          <p data-testid="home-events-empty" className="m-0 text-sm text-ink-2">
            {t('dashboard.events.empty')}
          </p>
        ) : (
          <ul data-testid="home-events-list" className="m-0 flex list-none flex-col gap-2.5 p-0">
            {visible.map((e) => (
              <li key={e.id} className="flex min-w-0 gap-2.5">
                <EventColorStripe slot={resolveStripeSlot(e.color_token)} />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-bold">{e.title}</span>
                  <span className="block text-caption text-ink-2 tnum">
                    {formatEventTimeRange(
                      new Date(e.starts_at),
                      e.ends_at ? new Date(e.ends_at) : null,
                      e.all_day,
                    )}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
        {overflow > 0 ? (
          <Link
            to="/kalender"
            data-testid="home-events-overflow"
            className="-mb-2 flex min-h-11 items-center text-caption font-semibold text-ink-2"
          >
            {t('calendar.eventsTile.overflow', { count: overflow })}
          </Link>
        ) : null}
      </section>
    </div>
  );
}
